<template>
  <section class="vacancies-slider">
    <div class="header">
      <span v-if="subtitle" class="subtitle">{{ subtitle }}</span>
      <h2 v-if="title" class="title">{{ title }}</h2>
    </div>

    <div class="navigator-wrapper">
      <div v-if="showTabs" class="tabs">
        <div
          v-for="tab in visibleTabs"
          :key="tab.id"
          class="tab"
          :class="{ '-active': currentTab === tab.id }"
          @click="setActiveTab(tab.id)"
        >
          {{ tab.title }}
        </div>
      </div>
      <div class="buttons">
        <BaseButton :id="`button-prev-${id}`" icon-left="arrow/arrow-left-1" size="xl" />
        <BaseButton :id="`button-next-${id}`" icon-left="arrow/arrow-right" size="xl" />
      </div>
    </div>

    <ClientOnly>
      <swiper-container ref="swiperRef" class="swiper" :init="false">
        <swiper-slide v-for="slide in currentList" :key="slide.id" class="swiper-slide">
          <VacancyNewCard :card="slide" />
        </swiper-slide>
      </swiper-container>
    </ClientOnly>

    <BaseButtonLink
      v-if="companySlug"
      :name="currentName"
      :link="currentLinkCompany"
      target="_blank"
    />
    <BaseButtonLink
      v-if="buttonLink"
      :name="currentName"
      :link="buttonLink"
      target="_blank"
      component-type="a"
    />
  </section>
</template>

<script lang="ts" setup>
  import VacancyNewCard from './VacancyNewCard.vue'
  import { BaseButton, BaseButtonLink } from '~/components/base-ui/buttons'
  import type { VacancyNewCardModel } from '~/models/vacancy'

  const props = withDefaults(
    defineProps<{
      vacancies?: VacancyNewCardModel[]
      internships?: VacancyNewCardModel[]
      companySlug?: string
      buttonLink?: string
      companyName?: string
      subtitle?: string
      title?: string
      showTabs?: boolean
    }>(),
    {
      vacancies: () => [],
      internships: () => [],
      companySlug: '',
      buttonLink: '',
      companyName: '',
      subtitle: '',
      title: '',
      showTabs: true,
    }
  )

  const visibleTabs = computed(() => {
    const tabs: { id: number; title: string }[] = []
    if (props.vacancies.length > 0) {
      tabs.push({ id: 1, title: 'Вакансии' })
    }
    if (props.internships.length > 0) {
      tabs.push({ id: 2, title: 'Стажировки' })
    }
    return tabs
  })

  const currentTab = ref(visibleTabs.value[0]?.id ?? 1)

  const currentList = computed(() => (currentTab.value === 2 ? props.internships : props.vacancies))

  const currentName = computed(() => (currentTab.value === 2 ? 'Все стажировки' : 'Все вакансии'))

  const currentLinkCompany = computed(() =>
    currentTab.value === 2
      ? `/stazhirovka?mf:organization_value.keyword=${props.companyName}`
      : `/vacancies/region-russia/kompaniya-${props.companySlug}`
  )

  const setActiveTab = (id: number) => {
    currentTab.value = id
  }

  const id = useId()
  const swiperRef = ref(null)

  const swiper = useSwiper(swiperRef, {
    slidesPerView: 'auto',
    spaceBetween: 20,
    navigation: {
      nextEl: `#button-next-${id}`,
      prevEl: `#button-prev-${id}`,
    },
  })

  watch(currentTab, () => {
    nextTick(() => swiper.to(0, 0))
  })
</script>

<style lang="scss" scoped>
  .vacancies-slider {
    display: grid;
    gap: 32px;
    width: 100%;
    padding: 52px 0 52px 52px;
    background-color: $color-default-white;
    border-radius: 40px;
    background-image:
      url('~/assets/images/pages/vacancy/gradients/gradient-top.svg'),
      url('~/assets/images/pages/vacancy/gradients/gradient-left.svg');
    background-repeat: no-repeat;
    background-position:
      top right,
      bottom left;

    @include vp-1439 {
      gap: 24px;
      padding: 32px 0 32px 32px;
      border-radius: 32px;
      background-position:
        top right -75px,
        bottom left;
      background-size:
        auto,
        auto 75%;
    }

    @include vp-767 {
      padding: 24px 0 24px 16px;
      border-radius: 24px;
      background-position:
        top right -50px,
        center left;
      background-size:
        50% auto,
        auto 60%;
    }

    > .header {
      padding-right: 52px;

      @include vp-1439 {
        padding-right: 32px;
      }

      @include vp-767 {
        padding-right: 16px;
      }
    }

    > .header > .subtitle {
      display: inline-block;
      margin-bottom: 6px;
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
      color: #7fea6e;

      @include vp-767 {
        font-size: 16px;
        line-height: 24px;
      }
    }

    > .header > .title {
      font-size: 40px;
      font-weight: 600;
      line-height: 48px;

      @include vp-1439 {
        font-size: 32px;
        line-height: 40px;
      }

      @include vp-767 {
        font-size: 24px;
        line-height: 32px;
      }
    }

    > .navigator-wrapper {
      display: flex;
      justify-content: space-between;
      gap: 32px;
      padding-right: 52px;

      @include vp-1439 {
        padding-right: 32px;
      }

      @include vp-767 {
        padding-right: 16px;
      }
    }

    > .navigator-wrapper > .tabs {
      display: flex;
      gap: 32px;
      width: 60%;
      border-bottom: 1px solid #e3e5e8;

      @include vp-1439 {
        width: 100%;
      }

      > .tab {
        padding-bottom: 20px;
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
        color: $color-potato-30;
        cursor: pointer;

        &.-active {
          color: $color-default-black;
          border-bottom: 4px solid $color-kiwi-50;
        }
      }
    }

    > .navigator-wrapper > .buttons {
      display: flex;
      gap: 12px;
      margin-left: auto;

      @include vp-1439 {
        display: none;
      }

      > .svoi-button {
        height: 48px;
        background-color: $color-default-black;
      }

      :deep(.swiper-button-lock) {
        display: none;
      }
    }

    > .svoi-button {
      margin: 0 auto;
      background-color: $color-kiwi-50;

      @include vp-767 {
        width: calc(100% - 16px);
        height: 48px;
      }
    }

    > .swiper {
      display: grid;
      width: 100%;

      .swiper-slide {
        width: 305px;
        height: auto;

        > .vacancy-new-card {
          height: 100%;
        }
      }
    }
  }
</style>
